<template>
  <div class="position-relative">
    <div
      v-if="isRefreshing"
      class="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
      style="z-index: 1; background: rgba(255, 255, 255, 0.6)"
    >
      <LoadingIndicatorComponent />
    </div>

    <PortfolioSection
      v-for="section in PORTFOLIO_SECTIONS"
      :key="section.id"
      :title="$t(section.titleKey)"
      :description="$t(section.descriptionKey)"
      :volume-label="$t(section.volumeLabelKey)"
      :portfolios="visiblePortfoliosBySection[section.id] ?? []"
      :all-portfolios="portfoliosBySection[section.id] ?? []"
      :hidden-count="hiddenCountBySection[section.id] ?? 0"
      :showing-hidden="showEmptySections.has(section.id)"
      :always-show="section.alwaysShow ?? false"
      :empty-message="
        section.emptyMessageKey ? $t(section.emptyMessageKey) : undefined
      "
      @show-hidden="toggleShowEmpty(section.id)"
    >
      <template v-if="section.headerLinkLabel" #header-action>
        <button
          class="btn btn-sm btn-outline-secondary d-flex align-items-center gap-2 ms-3 flex-shrink-0"
          @click="handleSectionNavigate(section)"
        >
          <span class="d-none d-sm-inline">
            {{ $t(section.headerLinkLabel) }}
          </span>
          <FontAwesomeIcon icon="fas fa-arrow-right" />
        </button>
      </template>

      <PortfolioCard
        v-for="portfolio in visiblePortfoliosBySection[section.id] ?? []"
        :key="portfolio.id"
        :portfolio="portfolio"
        :is-expanded="expandedPortfolioIds.has(portfolio.id)"
        @toggle="toggleExpand(portfolio.id)"
        @merge="openMergeModal"
        @split="openSplitModal"
        @move="openMoveModal"
        @navigate="handlePortfolioNavigate"
        @refresh="handleRefresh"
      />
    </PortfolioSection>

    <VirtualPortfolioMergeModal
      v-model="showMergeModal"
      :source-portfolio="mergeSourcePortfolio"
      :available-targets="availableMergeTargets"
      @success="handleRefresh"
    />

    <VirtualPortfolioSplitModal
      v-model="showSplitModal"
      :source-portfolio="splitSourcePortfolio"
      @success="handleRefresh"
    />

    <MoveItemsModal
      v-model="showMoveModal"
      :source-portfolio="moveSourcePortfolio"
      :available-targets="availableMoveTargets"
      @success="handleRefresh"
    />
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { usePortfolioItemsStore } from "@/dashboard/inventory_management/common/usePortfolioItemsStore";
import {
  PORTFOLIO_SECTIONS,
  PORTFOLIO_TYPE_CONFIG,
} from "@/dashboard/inventory_management/portfolios/portfolioConfig";
import type { PortfolioSectionConfig } from "@/dashboard/inventory_management/portfolios/portfolioConfig";
import { groupItemsIntoPortfolios } from "@/dashboard/inventory_management/portfolios/portfolioGrouping";
import type {
  PortfolioGroup,
  PortfolioSection as PortfolioSectionType,
  PortfolioType,
} from "@/dashboard/inventory_management/portfolios/types";
import type { FlattenedPortfolioItem } from "@/types";
import VirtualPortfolioMergeModal from "./components/VirtualPortfolioMergeModal.vue";
import VirtualPortfolioSplitModal from "./components/VirtualPortfolioSplitModal.vue";
import { useProductionStrategyStore } from "@/dashboard/production_strategy/useProductionStrategyStore";
import { useAccountStore } from "@/composables/account/useAccountStore";
import { readsForwardsAsStaff } from "@/forwards2/forwardsGuard";
import { useQueryManager } from "@/composables/useQueryManager";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import MoveItemsModal from "./components/MoveItemsModal.vue";
import PortfolioCard from "./components/PortfolioCard.vue";
import PortfolioSection from "./components/PortfolioSection.vue";

const portfolioItemsStore = usePortfolioItemsStore();
const productionStrategyStore = useProductionStrategyStore();
const router = useRouter();
const { updateQuery } = useQueryManager();
const accountStore = useAccountStore();

const expandedPortfolioIds = ref<Set<number>>(new Set());
const showEmptySections = ref<Set<PortfolioSectionType>>(new Set());
const isRefreshing = ref(false);

const showMergeModal = ref(false);
const mergeSourcePortfolio = ref<PortfolioGroup | null>(null);

const showSplitModal = ref(false);
const splitSourcePortfolio = ref<PortfolioGroup | null>(null);

const showMoveModal = ref(false);
const moveSourcePortfolio = ref<PortfolioGroup | null>(null);

const allPortfolios = computed((): PortfolioGroup[] => {
  return groupItemsIntoPortfolios(
    portfolioItemsStore.allItems as FlattenedPortfolioItem[]
  );
});

const portfoliosBySection = computed(() => {
  const result: Record<PortfolioSectionType, PortfolioGroup[]> = {
    inventory: [],
    spot: [],
    ppa: [],
    forwards: [],
  };

  for (const portfolio of allPortfolios.value) {
    const config = PORTFOLIO_TYPE_CONFIG[portfolio.portfolio_type];
    if (config) {
      result[config.section].push(portfolio);
    }
  }

  for (const key of Object.keys(result) as PortfolioSectionType[]) {
    result[key].sort((a, b) => b.total_volume - a.total_volume);
  }

  return result;
});

const visiblePortfoliosBySection = computed(() => {
  const result: Record<PortfolioSectionType, PortfolioGroup[]> = {
    inventory: [],
    spot: [],
    ppa: [],
    forwards: [],
  };

  for (const key of Object.keys(
    portfoliosBySection.value
  ) as PortfolioSectionType[]) {
    const all = portfoliosBySection.value[key];
    if (showEmptySections.value.has(key)) {
      result[key] = all;
    } else {
      const nonEmpty = all.filter((p) => p.total_volume > 0);
      result[key] = nonEmpty.length > 0 ? nonEmpty : all;
    }
  }

  return result;
});

const hiddenCountBySection = computed(() => {
  const result: Record<PortfolioSectionType, number> = {
    inventory: 0,
    spot: 0,
    ppa: 0,
    forwards: 0,
  };

  for (const key of Object.keys(
    portfoliosBySection.value
  ) as PortfolioSectionType[]) {
    if (showEmptySections.value.has(key)) continue;
    const all = portfoliosBySection.value[key];
    const nonEmpty = all.filter((p) => p.total_volume > 0);
    if (nonEmpty.length > 0 && nonEmpty.length < all.length) {
      result[key] = all.length - nonEmpty.length;
    }
  }

  return result;
});

const availableMergeTargets = computed(() => {
  if (!mergeSourcePortfolio.value) return [];
  const sourceCompanyId = (
    mergeSourcePortfolio.value.items[0] as FlattenedPortfolioItem | undefined
  )?.company_id;
  const sourceType = mergeSourcePortfolio.value.portfolio_type;
  return portfoliosBySection.value.inventory.filter(
    (p) =>
      p.id !== mergeSourcePortfolio.value!.id &&
      p.portfolio_type === sourceType &&
      (!sourceCompanyId ||
        (p.items[0] as FlattenedPortfolioItem | undefined)?.company_id ===
          sourceCompanyId)
  );
});

const availableMoveTargets = computed(() => {
  if (!moveSourcePortfolio.value) return [];
  const config =
    PORTFOLIO_TYPE_CONFIG[moveSourcePortfolio.value.portfolio_type];
  if (!config) return [];

  const allowedTypes = new Set(config.allowedMoveTargets);
  const sourceCompanyId = (
    moveSourcePortfolio.value.items[0] as FlattenedPortfolioItem | undefined
  )?.company_id;

  const fromItems = allPortfolios.value.filter(
    (p) =>
      allowedTypes.has(p.portfolio_type) &&
      p.id !== moveSourcePortfolio.value!.id &&
      (!sourceCompanyId ||
        (p.items[0] as FlattenedPortfolioItem | undefined)?.company_id ===
          sourceCompanyId)
  );

  const fromItemIds = new Set(fromItems.map((p) => p.id));
  const fromStore = productionStrategyStore.portfolios
    .filter(
      (p): p is typeof p & { id: number } =>
        p.id != null &&
        allowedTypes.has(p.portfolio_type as PortfolioType) &&
        p.id !== moveSourcePortfolio.value!.id &&
        !fromItemIds.has(p.id) &&
        (!sourceCompanyId || p.company_id === sourceCompanyId)
    )
    .map((p): PortfolioGroup => ({
      id: p.id,
      name: p.name ?? p.portfolio_type,
      portfolio_type: p.portfolio_type as PortfolioType,
      is_default: p.is_default,
      total_volume: 0,
      updated_at: "",
      items: [],
    }));

  return [...fromItems, ...fromStore];
});

const toggleExpand = (portfolioId: number) => {
  if (expandedPortfolioIds.value.has(portfolioId)) {
    expandedPortfolioIds.value.delete(portfolioId);
  } else {
    expandedPortfolioIds.value.add(portfolioId);
  }
};

const toggleShowEmpty = (sectionId: PortfolioSectionType) => {
  if (showEmptySections.value.has(sectionId)) {
    showEmptySections.value.delete(sectionId);
  } else {
    showEmptySections.value.add(sectionId);
  }
};

const openMergeModal = (portfolio: PortfolioGroup) => {
  mergeSourcePortfolio.value = portfolio;
  showMergeModal.value = true;
};

const openSplitModal = (portfolio: PortfolioGroup) => {
  splitSourcePortfolio.value = portfolio;
  showSplitModal.value = true;
};

const openMoveModal = (portfolio: PortfolioGroup) => {
  moveSourcePortfolio.value = portfolio;
  showMoveModal.value = true;
};

const handleRefresh = async () => {
  isRefreshing.value = true;
  try {
    const companyIds = [
      ...new Set(
        portfolioItemsStore.allItems.map(
          (i: FlattenedPortfolioItem) => i.company_id
        )
      ),
    ].filter((id): id is number => id != null);

    await portfolioItemsStore.refetchItems();
    if (companyIds.length > 0) {
      await productionStrategyStore.refetchPortfolios(companyIds);
    }
  } finally {
    isRefreshing.value = false;
  }
};

const handleSectionNavigate = (section: PortfolioSectionConfig) => {
  if (section.headerLinkRoute) {
    router.push({ path: section.headerLinkRoute });
  }
};

const handlePortfolioNavigate = (portfolio: PortfolioGroup) => {
  if (portfolio.portfolio_type === "PPA") {
    updateQuery({
      tab: "trading-strategy",
      "trading-strategy-active-tab": "overview",
      "edit-ppa-id": String(portfolio.id),
    });
  } else if (portfolio.portfolio_type === "FORWARD") {
    // The forwards pages are staff-only; a producer's forward sales live in the strategy overview.
    if (readsForwardsAsStaff(accountStore.account))
      router.push({ name: "ForwardsOverview" });
    else
      updateQuery({
        tab: "trading-strategy",
        "trading-strategy-active-tab": "overview",
      });
  }
};
</script>
